// modules/freshcards/src/core/overlays/registry.ts
//
// 2026-09-18 (PROMPT F 19 Phase 3b) — OverlayRegistry = central in-memory store
// for active overlays. Singleton (one registry per FreshCards instance).
//
// Lifecycle:
//   - register(overlay): validate + add + emit('overlay:registered')
//   - unregister(id): remove + emit('overlay:unregistered')
//   - get(id): retrieve full overlay
//   - list(): all registered overlays (with optional filter)
//   - resolve(userId?, projectId?, surfaceId?): cascade via resolver
//
// Events: see OverlayLifecycle — registry fires onRegister / onUnregister /
// onActivate / onDeactivate / onSuspend / onError.
//
// Resolver integration (Phase 3a): registry.resolve() delegates to
// resolveOverlay() with the registry's lookup function.

import type { AppOverlay, OverlayValidation } from '../../types/models/AppOverlay';
import type { FacePreferenceStorage, FaceScope } from './facePreference';
import type {
  OverlayFilter,
  OverlayLifecycleEvent,
  OverlayLifecycleHooks,
  OverlayScope,
  OverlayState,
  OverlayStateMap,
} from './lifecycle';
import { newStateMap, setState, getState } from './lifecycle';
import {
  DEFAULT_OVERLAY_ID,
  resolveOverlay,
  type ResolveResult,
} from './resolver';

// ---------------------------------------------------------------------------
// Result type — matches StorageResult style in src/core/storage/types.ts
// ---------------------------------------------------------------------------

export type RegistryResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Result of validating an Overlay manifest (used by import flow). */
type ValidationFailure = { ok: false; errors: string[] };

function validateOverlay(o: AppOverlay): OverlayValidation | ValidationFailure {
  const errors: string[] = [];
  if (!o || typeof o !== 'object') errors.push('overlay is not an object');
  if (typeof o.id !== 'string' || o.id.length === 0) errors.push('id required');
  if (typeof o.name !== 'string' || o.name.length === 0) errors.push('name required');
  if (typeof o.version !== 'string' || o.version.length === 0) errors.push('version required');
  if (!['theme', 'layout', 'bundle'].includes(o.flavor)) errors.push(`invalid flavor: ${o.flavor}`);
  if (!['proprietary', 'off-the-shelf', 'generic'].includes(o.distribution))
    errors.push(`invalid distribution: ${o.distribution}`);
  if (!o.author || typeof o.author.id !== 'string') errors.push('author.id required');
  if (!o.license || typeof o.license.spdx !== 'string') errors.push('license.spdx required');
  if (!o.compatibility || typeof o.compatibility.freshcards !== 'string')
    errors.push('compatibility.freshcards required');
  if (typeof o.bundleHash !== 'string') errors.push('bundleHash required');
  // Flavor-specific field presence
  if (o.flavor === 'theme' && !o.theme) errors.push('theme required for flavor=theme');
  if (o.flavor === 'layout' && !o.layout) errors.push('layout required for flavor=layout');
  if (o.flavor === 'bundle' && !o.theme && !o.layout)
    errors.push('bundle must contain at least theme or layout');
  const failure: ValidationFailure = { ok: false, errors };
  if (errors.length > 0) return failure;
  return { ok: true, overlay: o };
}

// ---------------------------------------------------------------------------
// OverlayRegistry interface
// ---------------------------------------------------------------------------

export interface OverlayRegistry {
  /** Register a new overlay. Validates against AppOverlay type. */
  register(overlay: AppOverlay): RegistryResult<AppOverlay>;
  /** Remove an overlay by id. */
  unregister(id: string): RegistryResult<true>;
  /** Get an overlay by id. */
  get(id: string): AppOverlay | null;
  /** List all overlays, optionally filtered. */
  list(filter?: OverlayFilter): AppOverlay[];
  /** Whether an overlay id is registered. */
  has(id: string): boolean;
  /**
   * Resolve the active overlay for a scope.
   * Cascade: surface > project > account > default.
   * Returns the default overlay (or empty fallback) if nothing registered.
   */
  resolve(
    userId?: string,
    projectId?: string,
    surfaceId?: string,
    storage?: FacePreferenceStorage,
  ): Promise<ResolveResult>;
  /** Set the default fallback overlay (used when nothing registered). */
  setDefault(overlay: AppOverlay): RegistryResult<AppOverlay>;
  /** Get the current default fallback overlay. */
  getDefault(): AppOverlay | null;
  /** Wire lifecycle hooks (one-shot per registry instance). */
  setHooks(hooks: Partial<OverlayLifecycleHooks>): void;
  /** Emit a lifecycle event (used internally; exposed for diagnostics). */
  emit(event: OverlayLifecycleEvent): void;
  /** Get current state of an overlay (debug/diagnostics). */
  getOverlayState(id: string): OverlayState;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function makeOverlayRegistry(): OverlayRegistry {
  const overlays: Map<string, AppOverlay> = new Map();
  const states: OverlayStateMap = newStateMap();
  let defaultOverlay: AppOverlay | null = null;
  let hooks: OverlayLifecycleHooks = {};
  const activeScopes: Map<string, OverlayScope> = new Map(); // overlayId → last activated scope

  function lookup(id: string): AppOverlay | null {
    if (overlays.has(id)) return overlays.get(id)!;
    if (defaultOverlay && defaultOverlay.id === id) return defaultOverlay;
    return null;
  }

  function emit(event: OverlayLifecycleEvent): void {
    // Fire-and-forget hooks. Hooks that throw are caught + logged.
    const fire = (fn: ((...args: unknown[]) => void | Promise<void>) | undefined, ...args: unknown[]) => {
      if (!fn) return;
      try {
        const r = (fn as (...a: unknown[]) => void | Promise<void>)(...args);
        if (r instanceof Promise) r.catch((e) => console.error('[overlay-registry] hook error', e));
      } catch (e) {
        console.error('[overlay-registry] hook error', e);
      }
    };

    switch (event.type) {
      case 'overlay:registered':
        fire(hooks.onRegister as (o: AppOverlay) => void, overlays.get(event.overlayId));
        break;
      case 'overlay:unregistered':
        fire(hooks.onUnregister as (id: string) => void, event.overlayId);
        break;
      case 'overlay:activated':
        fire(hooks.onActivate as (id: string, s: OverlayScope) => void, event.overlayId, event.scope);
        break;
      case 'overlay:deactivated':
        fire(hooks.onDeactivate as (id: string, s: OverlayScope) => void, event.overlayId, event.scope);
        break;
      case 'overlay:suspended':
        fire(hooks.onSuspend as (id: string, r: string) => void, event.overlayId, event.reason);
        break;
      case 'overlay:error':
        fire(hooks.onError as (id: string, e: string) => void, event.overlayId, event.error);
        break;
    }
  }

  const registry: OverlayRegistry = {
    register(overlay) {
      const v = validateOverlay(overlay);
      if (!v.ok) return { ok: false, error: `validation failed: ${v.errors.join(', ')}` };
      const o = v.overlay;
      if (overlays.has(o.id)) {
        return { ok: false, error: `overlay ${o.id} already registered (use unregister first)` };
      }
      overlays.set(o.id, o);
      setState(states, o.id, 'registered');
      emit({ type: 'overlay:registered', overlayId: o.id, ts: Date.now() });
      return { ok: true, value: o };
    },

    unregister(id) {
      if (!overlays.has(id)) return { ok: false, error: `overlay ${id} not registered` };
      overlays.delete(id);
      states.delete(id);
      activeScopes.delete(id);
      emit({ type: 'overlay:unregistered', overlayId: id, ts: Date.now() });
      return { ok: true, value: true };
    },

    get(id) {
      return overlays.get(id) ?? (defaultOverlay && defaultOverlay.id === id ? defaultOverlay : null);
    },

    list(filter) {
      const all = Array.from(overlays.values());
      if (defaultOverlay && !overlays.has(defaultOverlay.id)) all.push(defaultOverlay);
      if (!filter) return all;
      return all.filter((o) => {
        if (filter.flavor && o.flavor !== filter.flavor) return false;
        if (filter.author && o.author.id !== filter.author) return false;
        if (filter.distribution && o.distribution !== filter.distribution) return false;
        if (filter.tags && filter.tags.length > 0) {
          const otags = o.tags ?? [];
          if (!filter.tags.every((t) => otags.includes(t))) return false;
        }
        return true;
      });
    },

    has(id) {
      return overlays.has(id) || (defaultOverlay?.id === id);
    },

    async resolve(userId, projectId, surfaceId, storage) {
      const scope: FaceScope = { userId, projectId, surfaceId };
      if (!storage) {
        // No storage → can't read preferences → use default directly.
        if (defaultOverlay) {
          return {
            ok: true,
            resolved: {
              overlay: defaultOverlay,
              source: 'default',
              compatible: true,
            },
          };
        }
        return {
          ok: true,
          resolved: {
            overlay: emptyOverlay(),
            source: 'default',
            compatible: true,
          },
        };
      }

      const result = await resolveOverlay(storage, lookup, scope);

      // Track activation if a real overlay was hit.
      if (result.ok && result.resolved.overlay.id !== 'overlay.freshcards.empty') {
        const scopeForEvent: OverlayScope = { userId, projectId, surfaceId };
        const previousScope = activeScopes.get(result.resolved.overlay.id);
        if (
          !previousScope ||
          previousScope.userId !== scopeForEvent.userId ||
          previousScope.projectId !== scopeForEvent.projectId ||
          previousScope.surfaceId !== scopeForEvent.surfaceId
        ) {
          if (previousScope) {
            emit({
              type: 'overlay:deactivated',
              overlayId: result.resolved.overlay.id,
              scope: previousScope,
              ts: Date.now(),
            });
          }
          activeScopes.set(result.resolved.overlay.id, scopeForEvent);
          emit({
            type: 'overlay:activated',
            overlayId: result.resolved.overlay.id,
            scope: scopeForEvent,
            ts: Date.now(),
          });
          setState(states, result.resolved.overlay.id, 'active');
        }
        // Surface compatibility warnings as suspension events.
        if (!result.resolved.compatible && result.resolved.warning) {
          emit({
            type: 'overlay:suspended',
            overlayId: result.resolved.overlay.id,
            reason: 'incompatible_version',
            ts: Date.now(),
          });
          setState(states, result.resolved.overlay.id, 'suspended');
        }
      }

      return result;
    },

    setDefault(overlay) {
      const v = validateOverlay(overlay);
      if (!v.ok) return { ok: false, error: `validation failed: ${v.errors.join(', ')}` };
      defaultOverlay = v.overlay;
      // Default is implicitly registered — only set it if not already in the registry.
      if (!overlays.has(defaultOverlay.id)) {
        overlays.set(defaultOverlay.id, defaultOverlay);
        setState(states, defaultOverlay.id, 'registered');
      }
      return { ok: true, value: defaultOverlay };
    },

    getDefault() {
      return defaultOverlay;
    },

    setHooks(h) {
      hooks = { ...hooks, ...h };
    },

    emit,

    getOverlayState(id) {
      return getState(states, id);
    },
  };

  // Register the placeholder default id so list()/has() work before Phase 7 wires real.
  // Do NOT emit 'overlay:registered' — that's reserved for explicit register() calls.
  // The default is set lazily via setDefault().
  void DEFAULT_OVERLAY_ID; // referenced to keep import live

  return registry;
}

// ---------------------------------------------------------------------------
// Empty fallback (same shape as resolver's internal helper)
// ---------------------------------------------------------------------------

function emptyOverlay(): AppOverlay {
  return {
    id: 'overlay.freshcards.empty',
    name: 'Empty',
    version: '0.0.0',
    flavor: 'bundle',
    distribution: 'generic',
    author: { id: 'system', displayName: 'System' },
    license: { spdx: 'proprietary' },
    compatibility: { freshcards: '>=0.0.0' },
    bundleHash: '0000000000000000000000000000000000000000000000000000000000000000',
  };
}

// ---------------------------------------------------------------------------
// Module-level singleton (one per app instance)
// ---------------------------------------------------------------------------

let _singleton: OverlayRegistry | null = null;

export function getOverlayRegistry(): OverlayRegistry {
  if (!_singleton) _singleton = makeOverlayRegistry();
  return _singleton;
}

export function resetOverlayRegistry(): void {
  _singleton = null;
}
